/**
 * Porta de entrada do app — a única tela que uma pessoa vê antes de autenticar.
 * `GET /auth/status` decide o que mostrar: se não existe ninguém ainda
 * (`needsSetup`), convida a criar a primeira conta (que vira administradora);
 * senão mostra login, com link de registro só quando `signupEnabled`.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError, get } from '@/lib/api';
import type { AuthStatus } from '@/lib/types';
import { useAuth } from '@/hooks/useAuth';
import { maskPhone } from '@/lib/format';
import { Field } from '@/components/Field';
import { Button } from '@/components/Button';
import { Spinner } from '@/components/Spinner';
import './Login.css';

type Mode = 'login' | 'register';

interface LocationState {
  from?: string;
}

export function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const statusQuery = useQuery({
    queryKey: ['auth-status'],
    queryFn: () => get<AuthStatus>('/auth/status'),
  });

  const [mode, setMode] = useState<Mode>('login');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [name, setName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const needsSetup = statusQuery.data?.needsSetup ?? false;
  const signupEnabled = statusQuery.data?.signupEnabled ?? false;

  useEffect(() => {
    if (needsSetup) setMode('register');
  }, [needsSetup]);

  // Já autenticado (ex.: voltou para /entrar por engano) — não fica preso na tela de login.
  useEffect(() => {
    if (user) {
      const state = location.state as LocationState | null;
      navigate(state?.from && state.from !== '/entrar' ? state.from : '/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  function redirectAfterAuth() {
    const state = location.state as LocationState | null;
    navigate(state?.from && state.from !== '/entrar' ? state.from : '/', { replace: true });
  }

  function applyError(err: unknown, fallback: string) {
    if (err instanceof ApiError) {
      setFormError(err.message);
      setFieldErrors(err.fields ?? {});
    } else {
      setFormError(fallback);
      setFieldErrors({});
    }
  }

  async function handleLogin(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      await login({ email, password });
      redirectAfterAuth();
    } catch (err) {
      applyError(err, 'Não foi possível entrar agora. Tente de novo em instantes.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRegister(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});

    if (regPassword.length < 8) {
      setFieldErrors({ password: 'A senha precisa ter pelo menos 8 caracteres.' });
      return;
    }
    if (regPassword !== confirmPassword) {
      setFieldErrors({ confirmPassword: 'As senhas não coincidem.' });
      return;
    }

    setSubmitting(true);
    try {
      await register({
        name,
        email: regEmail,
        password: regPassword,
        phone: phone || undefined,
      });
      redirectAfterAuth();
    } catch (err) {
      applyError(err, 'Não foi possível criar a conta agora. Tente de novo em instantes.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-shell">
      <aside className="login-aside no-print">
        <p className="rail__brand-title">Central de Propostas</p>
        <p className="rail__brand-sub">Arkmeds</p>
        <p className="login-aside__pitch">
          Transforma um pedido de equipamentos em uma proposta comercial correta — cliente, imposto
          e preço vigente — em menos de dois minutos.
        </p>
      </aside>

      <div className="login-form-wrap">
        {statusQuery.isLoading ? (
          <Spinner label="Carregando…" />
        ) : statusQuery.isError ? (
          <div className="login-form stack">
            <p className="login-form__title display">Não foi possível carregar</p>
            <p className="muted">Verifique sua conexão e tente de novo.</p>
            <Button onClick={() => statusQuery.refetch()}>Tentar de novo</Button>
          </div>
        ) : needsSetup ? (
          <form className="login-form stack" onSubmit={handleRegister} noValidate>
            <div>
              <h1 className="login-form__title display">Criar a primeira conta</h1>
              <p className="login-form__meta">
                Ainda não existe nenhuma conta neste sistema. A primeira pessoa a se cadastrar vira
                administradora.
              </p>
            </div>
            {formError ? <p className="form-alert form-alert-error" role="alert">{formError}</p> : null}
            <Field
              label="Nome"
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={fieldErrors.name}
            />
            <Field
              label="E-mail"
              type="email"
              autoComplete="email"
              required
              value={regEmail}
              onChange={(e) => setRegEmail(e.target.value)}
              error={fieldErrors.email}
            />
            <Field
              label="Celular"
              type="tel"
              autoComplete="tel"
              hint="Opcional."
              value={phone}
              onChange={(e) => setPhone(maskPhone(e.target.value))}
              error={fieldErrors.phone}
            />
            <Field
              label="Senha"
              type="password"
              autoComplete="new-password"
              required
              hint="Mínimo de 8 caracteres."
              value={regPassword}
              onChange={(e) => setRegPassword(e.target.value)}
              error={fieldErrors.password}
            />
            <Field
              label="Confirmar senha"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={fieldErrors.confirmPassword}
            />
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Criando conta…' : 'Criar conta e entrar'}
            </Button>
          </form>
        ) : (
          <div className="login-form stack">
            {mode === 'login' ? (
              <form className="stack" onSubmit={handleLogin} noValidate>
                <div>
                  <h1 className="login-form__title display">Entrar</h1>
                  <p className="login-form__meta">Use seu e-mail e senha para acessar.</p>
                </div>
                {formError ? <p className="form-alert form-alert-error" role="alert">{formError}</p> : null}
                <Field
                  label="E-mail"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={fieldErrors.email}
                />
                <Field
                  label="Senha"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={fieldErrors.password}
                />
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Entrando…' : 'Entrar'}
                </Button>
              </form>
            ) : (
              <form className="stack" onSubmit={handleRegister} noValidate>
                <div>
                  <h1 className="login-form__title display">Criar conta</h1>
                  <p className="login-form__meta">Peça os dados de acesso a quem administra, se preferir.</p>
                </div>
                {formError ? <p className="form-alert form-alert-error" role="alert">{formError}</p> : null}
                <Field
                  label="Nome"
                  autoComplete="name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={fieldErrors.name}
                />
                <Field
                  label="E-mail"
                  type="email"
                  autoComplete="email"
                  required
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  error={fieldErrors.email}
                />
                <Field
                  label="Celular"
                  type="tel"
                  autoComplete="tel"
                  hint="Opcional."
                  value={phone}
                  onChange={(e) => setPhone(maskPhone(e.target.value))}
                  error={fieldErrors.phone}
                />
                <Field
                  label="Senha"
                  type="password"
                  autoComplete="new-password"
                  required
                  hint="Mínimo de 8 caracteres."
                  value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  error={fieldErrors.password}
                />
                <Field
                  label="Confirmar senha"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={fieldErrors.confirmPassword}
                />
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Criando conta…' : 'Criar conta'}
                </Button>
              </form>
            )}

            {signupEnabled ? (
              <p className="login-switch">
                {mode === 'login' ? (
                  <>
                    Ainda não tem conta?{' '}
                    <button type="button" onClick={() => { setMode('register'); setFormError(null); setFieldErrors({}); }}>
                      Criar conta
                    </button>
                  </>
                ) : (
                  <>
                    Já tem conta?{' '}
                    <button type="button" onClick={() => { setMode('login'); setFormError(null); setFieldErrors({}); }}>
                      Entrar
                    </button>
                  </>
                )}
              </p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
